// surface flooding (DWA-M 119 style): terrain grid, water from manhole overflow / areal rain / river high water, routed downhill until it ponds in hollows
import { netOk } from './netGuard';
import proj4 from 'proj4';
import { EPSG } from './georef';

const R = 6378137, mx = (lon) => (lon * Math.PI * R) / 180, my = (lat) => R * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
const lonOf = (x) => (x / R) * (180 / Math.PI), latOf = (y) => (2 * Math.atan(Math.exp(y / R)) - Math.PI / 2) * (180 / Math.PI);

// free terrain tiles (Terrarium PNG, CORS open) -> sampler(lat, lon)
export async function tileSampler([s, w, n, e], z = 14) {
  const tx = (lon) => ((lon + 180) / 360) * 2 ** z, ty = (lat) => ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) * 2 ** z;
  const x0 = Math.floor(tx(w)), x1 = Math.floor(tx(e)), y0 = Math.floor(ty(n)), y1 = Math.floor(ty(s)), tiles = {};
  await Promise.all([...Array(x1 - x0 + 1)].flatMap((_, i) => [...Array(y1 - y0 + 1)].map(async (__, j) => {
    const img = new Image(); img.crossOrigin = 'anonymous'; img.src = `https://s3.amazonaws.com/elevation-tiles-prod/terrarium/${z}/${x0 + i}/${y0 + j}.png`;
    await img.decode(); const c = document.createElement('canvas'); c.width = c.height = 256; const g = c.getContext('2d'); g.drawImage(img, 0, 0);
    tiles[`${x0 + i}/${y0 + j}`] = g.getImageData(0, 0, 256, 256).data;
  })));
  return (lat, lon) => { const fx = tx(lon), fy = ty(lat), d = tiles[`${Math.floor(fx)}/${Math.floor(fy)}`]; if (!d) return NaN; const k = (Math.floor((fy % 1) * 256) * 256 + Math.floor((fx % 1) * 256)) * 4; return d[k] * 256 + d[k + 1] + d[k + 2] / 256 - 32768; };
}

// own terrain model (DGM): GeoTIFF or ESRI ASCII grid in a projected CRS -> sampler(lat, lon)
export async function fileSampler(file, epsg) {
  let W, H, ox, oy, cs, data, nod = -9999, ep = epsg;
  if (/\.(tif|tiff)$/i.test(file.name)) {
    const { fromArrayBuffer } = await import('geotiff'); const im = await (await fromArrayBuffer(await file.arrayBuffer())).getImage();
    const gk = im.getGeoKeys() || {}; ep = epsg || gk.ProjectedCSTypeGeoKey || 25832;
    const [rx] = im.getResolution(), [bx, , , by] = im.getBoundingBox(); W = im.getWidth(); H = im.getHeight(); ox = bx; oy = by; cs = rx; data = (await im.readRasters({ interleave: true })); nod = Number(im.getGDALNoData?.() ?? -9999);
  } else {
    const txt = await file.text(), L = txt.split(/\r?\n/), hd = {}; let i = 0;
    for (; i < 6; i++) { const [k, v] = L[i].trim().split(/\s+/); if (!/^[a-z_]+$/i.test(k)) break; hd[k.toLowerCase()] = +v; }
    W = hd.ncols; H = hd.nrows; cs = hd.cellsize; ox = hd.xllcorner ?? hd.xllcenter - cs / 2; oy = (hd.yllcorner ?? hd.yllcenter - cs / 2) + H * cs; nod = hd.nodata_value ?? -9999;
    data = new Float32Array(W * H); let k = 0; L.slice(i).forEach((l) => l.trim() && l.trim().split(/\s+/).forEach((v) => { data[k++] = +v; }));
  }
  const def = EPSG[ep]; if (!def) throw new Error(`EPSG ${ep}`);
  const bb = [proj4(def, EPSG[4326], [ox, oy - H * cs]), proj4(def, EPSG[4326], [ox + W * cs, oy])];
  const f = (lat, lon) => { const [x, y] = proj4(EPSG[4326], def, [lon, lat]), c = Math.floor((x - ox) / cs), r = Math.floor((oy - y) / cs); if (c < 0 || r < 0 || c >= W || r >= H) return NaN; const v = data[r * W + c]; return v === nod ? NaN : v; };
  f.bbox = [bb[0][1], bb[0][0], bb[1][1], bb[1][0]]; f.cell = cs;
  return f;
}

// regular grid in web-mercator metres over the bbox
export function makeGrid(sample, [s, w, n, e], cell) {
  const X0 = mx(w), X1 = mx(e), Y0 = my(s), Y1 = my(n), k = 1 / Math.cos((((s + n) / 2) * Math.PI) / 180), c = cell * k;
  const W = Math.max(2, Math.round((X1 - X0) / c)), H = Math.max(2, Math.round((Y1 - Y0) / c)), z = new Float32Array(W * H);
  for (let r = 0; r < H; r++) for (let q = 0; q < W; q++) { const v = sample(latOf(Y1 - (r + 0.5) * c), lonOf(X0 + (q + 0.5) * c)); z[r * W + q] = Number.isFinite(v) ? v : NaN; }
  for (let i = 0; i < z.length; i++) if (!Number.isFinite(z[i])) z[i] = 1e4;
  return { W, H, z, X0, Y1, c, cell, at: (lat, lon) => { const q = Math.floor((mx(lon) - X0) / c), r = Math.floor((Y1 - my(lat)) / c); return q >= 0 && r >= 0 && q < W && r < H ? r * W + q : -1; }, bounds: [[latOf(Y1 - H * c), w], [n, lonOf(X0 + W * c)]] };
}

const N4 = (W) => [-1, 1, -W, W];
// river high water: every cell takes the water level of its NEAREST river section (multi-source BFS), so an upstream
// level never spills into lower ground further downstream; ground more than 1 m below the section's bed belongs to another
// (lower) water course, so a hillside stream does not flood the valley; protected sections (rise <= dyke) claim their area dry
function riverFlood(G, seeds) {
  const n = G.z.length, lv = new Float32Array(n).fill(-1e9), own = new Float32Array(n).fill(NaN), bs = new Float32Array(n), wet = new Uint8Array(n), q = new Int32Array(n);
  let h = 0, t = 0;
  seeds.forEach(([i, l, w, b]) => { if (Number.isNaN(own[i]) || l < own[i]) { if (Number.isNaN(own[i])) q[t++] = i; own[i] = l; bs[i] = b; wet[i] = w ? 1 : 0; } });
  while (h < t) {
    const i = q[h++], x = i % G.W;
    if (wet[i]) lv[i] = own[i];
    for (const d of N4(G.W)) { const j = i + d; if (j < 0 || j >= n || (d === -1 && x === 0) || (d === 1 && x === G.W - 1) || !Number.isNaN(own[j]) || G.z[j] >= own[i] || G.z[j] < bs[i] - 1) continue; own[j] = own[i]; bs[j] = bs[i]; wet[j] = wet[i]; q[t++] = j; }
  }
  let dry = 0; for (let i = 0; i < n; i++) if (!wet[i] && own[i] - G.z[i] >= 0.03) dry += G.cell * G.cell;
  return { lv, own, dry };
}
// seed cells along every river piece: base = lowest terrain within ~30 m (DEM at the line is often the bank), never rising
// downstream (OSM ways run in flow direction); rivers/canals rise fully, streams by a quarter (max 0.5 m)
export function riverSeeds(G, rivers, rise, dyke = 0) {
  const rc = Math.max(1, Math.round(30 / G.cell)), out = [];
  const low = (i) => { let m = G.z[i]; const x = i % G.W, y = (i / G.W) | 0; for (let dy = -rc; dy <= rc; dy++) for (let dx = -rc; dx <= rc; dx++) { const xx = x + dx, yy = y + dy; if (xx >= 0 && yy >= 0 && xx < G.W && yy < G.H) m = Math.min(m, G.z[yy * G.W + xx]); } return m; };
  rivers.forEach((r) => {
    const up = r.kind === 'stream' ? Math.min(rise * 0.25, 0.5) : rise, w = up > (r.dyke ?? dyke);
    let prev = Infinity;
    r.pts.forEach((p, k) => {
      const q = r.pts[k + 1] || p, dm = Math.hypot((q[0] - p[0]) * 111320, (q[1] - p[1]) * 111320 * Math.cos((p[0] * Math.PI) / 180)), st = Math.max(1, Math.ceil(dm / (G.cell * 0.5)));
      for (let i = 0; i < st; i++) { const j = G.at(p[0] + ((q[0] - p[0]) * i) / st, p[1] + ((q[1] - p[1]) * i) / st); if (j < 0 || G.z[j] > 9000) continue; prev = Math.min(prev, low(j)); out.push([j, prev + up, w, prev]); }
    });
  });
  return out;
}

// cellular routing: water moves to the lowest neighbour water surface; boundary cells drain; returns max depth and flow
export async function routeWater(G, src, { onStep } = {}) {
  const { W, H, z } = G, n = z.length, h = new Float32Array(n), mxh = new Float32Array(n), flux = new Float32Array(n), A = G.cell * G.cell;
  let vin = 0, edge = 0;
  src.forEach(([i, vol]) => { if (i >= 0) { h[i] += vol / A; vin += vol; } });
  const it = Math.min(2500, 3 * (W + H));
  for (let k = 0; k < it; k++) {
    for (let i = 0; i < n; i++) {
      if (h[i] < 1e-4) continue;
      const x = i % W, y = (i / W) | 0;
      if (x === 0 || y === 0 || x === W - 1 || y === H - 1) { edge += h[i] * A; h[i] = 0; continue; }
      const s = z[i] + h[i]; let b = -1, bs = s, sj;
      sj = z[i - 1] + h[i - 1]; if (sj < bs) { bs = sj; b = i - 1; }
      sj = z[i + 1] + h[i + 1]; if (sj < bs) { bs = sj; b = i + 1; }
      sj = z[i - W] + h[i - W]; if (sj < bs) { bs = sj; b = i - W; }
      sj = z[i + W] + h[i + W]; if (sj < bs) { bs = sj; b = i + W; }
      if (b < 0) continue;
      const m = Math.min(h[i], (s - bs) / 2); h[i] -= m; h[b] += m; flux[i] += m;
    }
    if (k % 25 === 0) { for (let i = 0; i < n; i++) if (h[i] > mxh[i]) mxh[i] = h[i]; onStep?.(k / it); await new Promise((r) => setTimeout(r, 0)); }
  }
  let stand = 0;
  for (let i = 0; i < n; i++) { if (h[i] > mxh[i]) mxh[i] = h[i]; stand += h[i] * A; }
  return { depth: mxh, end: h, flux, bal: { in: vin, edge, stand } };
}

// depth raster -> transparent PNG (sewer overflow: blue + light cyan flow paths, river high water: violet)
const PAL = { sew: [[30, 144, 255], [10, 34, 150]], river: [[167, 139, 250], [76, 29, 149]] };
export function floodImage(G, { depth, flux }, kind = 'sew', pathMin = 0.05) {
  const c = document.createElement('canvas'); c.width = G.W; c.height = G.H; const g = c.getContext('2d'), im = g.createImageData(G.W, G.H), [a, b] = PAL[kind];
  let area = 0, maxD = 0, deep = 0, vol = 0;
  for (let i = 0; i < depth.length; i++) {
    const d = depth[i], o = i * 4;
    if (d >= 0.03) { const t = Math.min(1, d / 1.0); for (let k = 0; k < 3; k++) im.data[o + k] = a[k] + (b[k] - a[k]) * t; im.data[o + 3] = 120 + 110 * t; area += G.cell * G.cell; vol += d * G.cell * G.cell; maxD = Math.max(maxD, d); if (d >= 0.1) deep += G.cell * G.cell; }
    else if (flux && flux[i] > pathMin) { im.data[o] = 103; im.data[o + 1] = 232; im.data[o + 2] = 249; im.data[o + 3] = 150; }
  }
  g.putImageData(im, 0, 0);
  return { url: c.toDataURL('image/png'), bounds: G.bounds, area, maxD, deep, vol };
}
// river high water alone: water depth above ground from the bathtub levels
export const riverDepth = (G, R) => G.z.map((z, i) => Math.max(0, R.lv[i] - z));
// flooded hollows: connected cells deeper than min (4 neighbours), the n deepest with deepest point, volume and area
export function hollows(G, depth, n = 5, min = 0.1) {
  const { W, H } = G, seen = new Uint8Array(depth.length), A = G.cell * G.cell, out = [], q = [];
  for (let s = 0; s < depth.length; s++) {
    if (seen[s] || depth[s] < min || G.z[s] > 9000) continue;
    let vol = 0, cnt = 0, best = s; seen[s] = 1; q.length = 0; q.push(s);
    while (q.length) {
      const i = q.pop(), x = i % W; vol += depth[i] * A; cnt += 1; if (depth[i] > depth[best]) best = i;
      for (const j of [x > 0 ? i - 1 : -1, x < W - 1 ? i + 1 : -1, i - W, i + W]) if (j >= 0 && j < W * H && !seen[j] && depth[j] >= min) { seen[j] = 1; q.push(j); }
    }
    const r = (best / W) | 0, c = best % W;
    out.push({ lat: +latOf(G.Y1 - (r + 0.5) * G.c).toFixed(6), lon: +lonOf(G.X0 + (c + 0.5) * G.c).toFixed(6), maxD: depth[best], vol, area: cnt * A });
  }
  return out.sort((a, b) => b.maxD - a.maxD).slice(0, n).map((h, i) => ({ ...h, no: i + 1 }));
}

// rivers and streams from OpenStreetMap for the area (online)
const OVERPASS = ['https://overpass-api.de/api/interpreter', 'https://maps.mail.ru/osm/tools/overpass/api/interpreter', 'https://overpass.private.coffee/api/interpreter', 'https://overpass.kumi.systems/api/interpreter'];
const wait = (ms) => new Promise((r) => { setTimeout(r, ms); });
// public Overpass servers are often overloaded (429/504): geometry clipped to the project area, all mirrors, then 2 more rounds with a pause
export async function fetchRivers([s, w, n, e], onTry) {
  const bb = `${s.toFixed(5)},${w.toFixed(5)},${n.toFixed(5)},${e.toFixed(5)}`, q = `[out:json][timeout:40];way["waterway"~"^(river|stream|canal)$"](${bb});out geom(${bb});`;
  if (!netOk(OVERPASS[0]) || navigator.onLine === false) return null;
  for (let round = 0; round < 3; round += 1) {
    if (round) { onTry?.(round + 1); await wait(round * 8000); }
    for (const u of OVERPASS) {
      try {
        const r = await fetch(u, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: `data=${encodeURIComponent(q)}`, signal: AbortSignal.timeout(45000) });
        if (!r.ok) continue;
        const j = await r.json().catch(() => null);
        if (!Array.isArray(j?.elements) || (j.remark && /error|timed out|out of memory/i.test(j.remark) && !j.elements.length)) continue;
        // clipped geometry has null for nodes outside the area -> split into separate pieces
        return j.elements.flatMap((x) => (x.geometry || []).reduce((a, g) => { if (g) a[a.length - 1].push([g.lat, g.lon]); else if (a[a.length - 1].length) a.push([]); return a; }, [[]]).filter((p) => p.length > 1).map((pts) => ({ name: x.tags?.name || x.tags?.waterway, kind: x.tags?.waterway, pts: pts.map(([a, b]) => [+a.toFixed(6), +b.toFixed(6)]) })));
      } catch { /* next mirror */ }
    }
  }
  return null;
}
export { riverFlood };
